// ----------------------------------------------------------------------------
// auto-editor — Inline automation curve editor panel for DAWSEUR
//
// Uses Akima spline interpolation — curves pass through all knots exactly
// and never overshoot. No bezier control point handles needed.
// Supports: add/remove knots, drag knots, step grid, value display.
// All colors come from theme CSS variables (--c0 to --cf).
// ----------------------------------------------------------------------------

const { automationClips, invalidateInterpolator } = require('./automation.js');
const { sampleCurve } = require('./akima.js');

function createAutoEditor(opts = {}) {
   const container = document.getElementById('lw-auto-body');
   if (!container) return null;

   const state = {
      currentClip: null,
      currentClipKey: null,
      onClipChanged: opts.onClipChanged || (() => {}),
      onBeforeEdit: opts.onBeforeEdit || (() => {}),
   };

   // ---- Theme color helper ----
   function getCSS(varName, fallback) {
      const v = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
      return v || fallback;
   }
   function theme() {
      return {
         bg:      getCSS('--c0', '#0a0a0a'),
         bg2:     getCSS('--c1', '#141414'),
         grid:    getCSS('--c2', '#1e1e1e'),
         gridMajor: getCSS('--c3', '#2d2d2d'),
         text:    getCSS('--ce', '#d2d2d2'),
         textBr:  getCSS('--cf', '#e1e1e1'),
         accent:  getCSS('--cb', '#a5a5a5'),
         muted:   getCSS('--c6', '#5a5a5a'),
         panel:   getCSS('--c4', '#333333'),
      };
   }

   // ---- Build the editor UI ----
   container.innerHTML = '';

   const wrap = document.createElement('div');
   wrap.className = 'lw-auto-editor';

   // Header
   const header = document.createElement('div');
   header.className = 'lw-auto-editor-header';
   const title = document.createElement('span');
   title.className = 'lw-auto-editor-title';
   title.textContent = 'Select an automation track';
   header.appendChild(title);
   wrap.appendChild(header);

   // Canvas area
   const canvasWrap = document.createElement('div');
   canvasWrap.className = 'lw-auto-editor-canvas-wrap';

   const canvas = document.createElement('canvas');
   canvas.className = 'lw-auto-editor-canvas';
   canvasWrap.appendChild(canvas);
   wrap.appendChild(canvasWrap);

   // Controls
   const controls = document.createElement('div');
   controls.className = 'lw-auto-editor-controls';

   const addPointBtn = document.createElement('button');
   addPointBtn.textContent = '+ Knot';
   addPointBtn.title = 'Add a knot at the largest gap';
   controls.appendChild(addPointBtn);

   const deletePointBtn = document.createElement('button');
   deletePointBtn.textContent = 'Del Knot';
   deletePointBtn.title = 'Delete selected knot';
   controls.appendChild(deletePointBtn);

   const clearBtn = document.createElement('button');
   clearBtn.textContent = 'Reset';
   clearBtn.title = 'Reset to flat line';
   controls.appendChild(clearBtn);

   const lengthLabel = document.createElement('span');
   lengthLabel.textContent = ' Len:';
   controls.appendChild(lengthLabel);

   const lengthInput = document.createElement('input');
   lengthInput.type = 'number';
   lengthInput.min = '0.25';
   lengthInput.max = '9999';
   lengthInput.step = '0.25';
   lengthInput.value = '4';
   lengthInput.style.width = '50px';
   controls.appendChild(lengthInput);

   const valDisplay = document.createElement('span');
   valDisplay.textContent = '--';
   valDisplay.style.marginLeft = '8px';
   controls.appendChild(valDisplay);

   wrap.appendChild(controls);
   container.appendChild(wrap);

   const ctx = canvas.getContext('2d');

   // ---- Editor state ----
   let points = [];
   let min = 0, max = 1, step = 0.01;
   let selectedPoint = -1;
   let draggingPoint = -1;
   let hoverPoint = -1;
   const PAD = 24;
   const PAD_TOP = 30;

   // ---- Coordinate helpers ----
   function xToPx(nx) { return PAD + nx * (canvas.width - 2 * PAD); }
   function yToPx(ny) { return PAD_TOP + (1 - ny) * (canvas.height - PAD_TOP - PAD); }
   function pxToX(px) { return (px - PAD) / (canvas.width - 2 * PAD); }
   function pxToY(py) { return 1 - (py - PAD_TOP) / (canvas.height - PAD_TOP - PAD); }
   function valToNorm(v) { return (v - min) / (max - min); }
   function normToVal(ny) { return min + ny * (max - min); }
   function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
   function formatVal(v) {
      if (Math.abs(max - min) > 100) return v.toFixed(1);
      if (Math.abs(max - min) > 1) return v.toFixed(2);
      return Math.round(v * 100) + '%';
   }

   // ---- Drawing ----
   function draw() {
      const c = theme();
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = c.bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const innerW = canvas.width - 2 * PAD;
      const innerH = canvas.height - PAD_TOP - PAD;
      const clipLen = state.currentClip ? (state.currentClip.lengthBeats || 4) : 4;

      // Step grid (beat-based, 16th notes)
      const totalSteps = Math.max(1, Math.ceil(clipLen * 4));
      for (let i = 0; i <= totalSteps; i++) {
         const x = PAD + (i / totalSteps) * innerW;
         const isMeasure = (i % 16) === 0;
         const isBeat = (i % 4) === 0;
         if (isMeasure) { ctx.strokeStyle = c.gridMajor; ctx.lineWidth = 1.5; }
         else if (isBeat) { ctx.strokeStyle = c.grid; ctx.lineWidth = 1; }
         else { ctx.strokeStyle = c.bg2; ctx.lineWidth = 0.5; }
         ctx.beginPath();
         ctx.moveTo(x, PAD_TOP);
         ctx.lineTo(x, canvas.height - PAD);
         ctx.stroke();
      }

      // Horizontal value lines (8 divisions)
      const valSteps = 8;
      for (let i = 0; i <= valSteps; i++) {
         const y = PAD_TOP + (i / valSteps) * innerH;
         ctx.strokeStyle = (i === valSteps / 2) ? c.grid : c.bg2;
         ctx.lineWidth = (i === valSteps / 2) ? 1 : 0.5;
         ctx.beginPath();
         ctx.moveTo(PAD, y);
         ctx.lineTo(canvas.width - PAD, y);
         ctx.stroke();
      }

      // Beat labels
      ctx.fillStyle = c.muted;
      ctx.font = '9px monospace';
      ctx.textAlign = 'center';
      for (let b = 0; b <= Math.ceil(clipLen); b++) {
         const x = PAD + (b / clipLen) * innerW;
         ctx.fillText(String(b + 1), x, PAD_TOP - 6);
      }

      // Value axis labels
      ctx.fillStyle = c.muted;
      ctx.font = '9px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(formatVal(max), PAD - 3, PAD_TOP + 8);
      ctx.fillText(formatVal(min), PAD - 3, canvas.height - PAD + 2);
      ctx.fillText(formatVal((min + max) / 2), PAD - 3, yToPx(0.5) + 3);

      // Border
      ctx.strokeStyle = c.grid;
      ctx.lineWidth = 1;
      ctx.strokeRect(PAD, PAD_TOP, innerW, innerH);

      if (points.length < 2) {
         ctx.fillStyle = c.muted;
         ctx.font = '13px monospace';
         ctx.textAlign = 'center';
         ctx.fillText('No automation clip selected', canvas.width / 2, canvas.height / 2);
         return;
      }

      // Draw curve using Akima spline sampling
      const sortedPts = points.slice().sort((a, b) => a.x - b.x);
      const numSamples = Math.max(60, Math.floor(innerW / 2));
      const sampled = sampleCurve(sortedPts, numSamples);

      ctx.strokeStyle = c.accent;
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i < sampled.length; i++) {
         const ny = clamp(valToNorm(sampled[i].y), 0, 1);
         const px = xToPx(sampled[i].x);
         const py = yToPx(ny);
         if (i === 0) ctx.moveTo(px, py);
         else ctx.lineTo(px, py);
      }
      ctx.stroke();

      // Draw knots
      for (let i = 0; i < points.length; i++) {
         const p = points[i];
         const ny = clamp(valToNorm(p.y), 0, 1);
         const px = xToPx(p.x);
         const py = yToPx(ny);
         const isSel = selectedPoint === i;
         const isHover = hoverPoint === i;

         ctx.fillStyle = isSel ? c.textBr : (isHover ? c.text : c.accent);
         ctx.beginPath();
         ctx.arc(px, py, isSel ? 5 : (isHover ? 4.5 : 3.5), 0, Math.PI * 2);
         ctx.fill();

         if (isSel) {
            ctx.strokeStyle = c.accent;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(px, py, 8, 0, Math.PI * 2);
            ctx.stroke();
         }
      }
   }

   // ---- Mouse interaction ----
   function getMousePos(e) {
      const rect = canvas.getBoundingClientRect();
      const sx = canvas.width / rect.width;
      const sy = canvas.height / rect.height;
      return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
   }

   function findPointAt(mx, my) {
      for (let i = points.length - 1; i >= 0; i--) {
         const p = points[i];
         const ny = clamp(valToNorm(p.y), 0, 1);
         const px = xToPx(p.x);
         const py = yToPx(ny);
         if (Math.abs(mx - px) < 8 && Math.abs(my - py) < 8) return i;
      }
      return -1;
   }

   canvas.addEventListener('mousedown', (e) => {
      if (!state.currentClip) return;
      const { x, y } = getMousePos(e);
      const idx = findPointAt(x, y);
      if (idx >= 0) {
         state.onBeforeEdit();
         selectedPoint = idx;
         draggingPoint = idx;
         updateControls();
         draw();
         return;
      }
      // Add new knot
      state.onBeforeEdit();
      const nx = clamp(pxToX(x), 0, 1);
      const ny = clamp(pxToY(y), 0, 1);
      if (nx < 0 || nx > 1) return;
      const newPoint = { x: nx, y: normToVal(ny) };
      let insertIdx = points.length;
      for (let i = 0; i < points.length; i++) {
         if (points[i].x > nx) { insertIdx = i; break; }
      }
      points.splice(insertIdx, 0, newPoint);
      selectedPoint = insertIdx;
      draggingPoint = insertIdx;
      saveToClip();
      updateControls();
      draw();
   });

   canvas.addEventListener('mousemove', (e) => {
      if (!state.currentClip) return;
      const { x, y } = getMousePos(e);
      if (draggingPoint >= 0) {
         const p = points[draggingPoint];
         let nx = clamp(pxToX(x), 0, 1);
         const ny = clamp(pxToY(y), 0, 1);
         if (draggingPoint > 0 && nx <= points[draggingPoint - 1].x) nx = points[draggingPoint - 1].x + 0.001;
         if (draggingPoint < points.length - 1 && nx >= points[draggingPoint + 1].x) nx = points[draggingPoint + 1].x - 0.001;
         p.x = nx;
         p.y = normToVal(ny);
         valDisplay.textContent = formatVal(p.y);
         saveToClip();
         draw();
      } else {
         hoverPoint = findPointAt(x, y);
         canvas.style.cursor = hoverPoint >= 0 ? 'pointer' : 'crosshair';
         if (hoverPoint >= 0 && points[hoverPoint]) {
            valDisplay.textContent = formatVal(points[hoverPoint].y);
         }
         draw();
      }
   });

   window.addEventListener('mouseup', () => {
      if (draggingPoint >= 0) {
         draggingPoint = -1;
         saveToClip();
      }
   });

   canvas.addEventListener('mouseleave', () => {
      hoverPoint = -1;
      canvas.style.cursor = 'default';
      draw();
   });

   // Double-click to add a knot
   canvas.addEventListener('dblclick', (e) => {
      if (!state.currentClip) return;
      const { x, y } = getMousePos(e);
      if (findPointAt(x, y) >= 0) return;
      const nx = clamp(pxToX(x), 0, 1);
      const ny = clamp(pxToY(y), 0, 1);
      if (nx < 0 || nx > 1) return;
      state.onBeforeEdit();
      const newPoint = { x: nx, y: normToVal(ny) };
      let insertIdx = points.length;
      for (let i = 0; i < points.length; i++) {
         if (points[i].x > nx) { insertIdx = i; break; }
      }
      points.splice(insertIdx, 0, newPoint);
      selectedPoint = insertIdx;
      saveToClip();
      updateControls();
      draw();
   });

   // Right-click on a knot to remove it (prevent default context menu)
   canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      if (!state.currentClip) return;
      const { x, y } = getMousePos(e);
      const idx = findPointAt(x, y);
      if (idx >= 0 && points.length > 2) {
         state.onBeforeEdit();
         points.splice(idx, 1);
         selectedPoint = -1;
         saveToClip();
         updateControls();
         draw();
      }
   });

   // ---- Buttons ----
   addPointBtn.addEventListener('click', () => {
      if (!state.currentClip) return;
      state.onBeforeEdit();
      let bestGap = 0, bestX = 0.5;
      for (let i = 0; i < points.length - 1; i++) {
         const gap = points[i + 1].x - points[i].x;
         if (gap > bestGap) { bestGap = gap; bestX = points[i].x + gap * 0.5; }
      }
      if (points.length === 0) bestX = 0.5;
      let val = normToVal(0.5);
      if (points.length >= 2) {
         for (let i = 0; i < points.length - 1; i++) {
            if (bestX >= points[i].x && bestX <= points[i + 1].x) {
               const t = (bestX - points[i].x) / (points[i + 1].x - points[i].x);
               val = points[i].y + (points[i + 1].y - points[i].y) * t;
               break;
            }
         }
      }
      const newP = { x: bestX, y: val };
      points.push(newP);
      points.sort((a, b) => a.x - b.x);
      selectedPoint = points.indexOf(newP);
      saveToClip();
      updateControls();
      draw();
   });

   deletePointBtn.addEventListener('click', () => {
      if (!state.currentClip || selectedPoint < 0) return;
      state.onBeforeEdit();
      if (points.length <= 2) return;
      points.splice(selectedPoint, 1);
      selectedPoint = -1;
      saveToClip();
      updateControls();
      draw();
   });

   clearBtn.addEventListener('click', () => {
      if (!state.currentClip) return;
      state.onBeforeEdit();
      const val = min + (max - min) * 0.5;
      points = [ { x: 0, y: val }, { x: 1, y: val } ];
      selectedPoint = -1;
      saveToClip();
      updateControls();
      draw();
   });

   lengthInput.addEventListener('change', () => {
      if (!state.currentClip) return;
      state.onBeforeEdit();
      const v = parseFloat(lengthInput.value);
      if (v > 0) {
         state.currentClip.lengthBeats = v;
         state.onClipChanged(state.currentClip);
         draw();
      }
   });

   // ---- Save points back to clip ----
   function saveToClip() {
      if (!state.currentClip) return;
      state.currentClip.points = points.map(p => ({ ...p }));
      invalidateInterpolator(state.currentClip);
      state.onClipChanged(state.currentClip);
   }

   // ---- Update controls state ----
   function updateControls() {
      const enabled = !!state.currentClip;
      addPointBtn.disabled = !enabled;
      deletePointBtn.disabled = !enabled || selectedPoint < 0 || points.length <= 2;
      clearBtn.disabled = !enabled;
      lengthInput.disabled = !enabled;
   }

   // ---- Load a clip into the editor ----
   function loadClip(clip) {
      // Handle autoTrack objects (from track-list) that wrap a clip
      if (clip && clip.clip && !clip.points) clip = clip.clip;
      state.currentClip = clip;
      state.currentClipKey = clip ? (clip.trackId + ':' + clip.paramName) : null;
      if (clip) {
         if (!clip.points || !Array.isArray(clip.points)) clip.points = [{ x: 0, y: 0 }, { x: 1, y: 0 }];
         points = clip.points.map(p => ({ ...p }));
         min = clip.min !== undefined ? clip.min : 0;
         max = clip.max !== undefined ? clip.max : 1;
         step = clip.step || 0.01;
         title.textContent = 'Automation — ' + clip.paramName;
         lengthInput.value = clip.lengthBeats || 4;
         valDisplay.textContent = formatVal(points[0] ? points[0].y : 0);
      } else {
         points = [];
         title.textContent = 'Select an automation track';
         valDisplay.textContent = '--';
      }
      selectedPoint = -1;
      updateControls();
      draw();
   }

   // ---- Resize canvas to fit container ----
   function resize() {
      const rect = canvasWrap.getBoundingClientRect();
      canvas.width = Math.max(200, Math.floor(rect.width));
      canvas.height = Math.max(150, Math.floor(rect.height));
      draw();
   }

   if (window.ResizeObserver) {
      const ro = new ResizeObserver(() => resize());
      ro.observe(canvasWrap);
   }

   return { loadClip, resize, draw, state };
}

module.exports = { createAutoEditor };
